import { create } from "zustand";
import type { LODLevel } from "@mind-sands/shared";
import type {
  DragPreviewState,
  EditMode,
  SidePanelTab,
  TransformMode,
} from "./types";

export interface UiStore {
  selectedObjectId: string | null;
  isTransformDragging: boolean;
  modelLibraryOpen: boolean;
  sidePanelOpen: boolean;
  sidePanelTab: SidePanelTab;
  dragPreview: DragPreviewState | null;
  immersiveMode: boolean;
  immersiveToolbarMinimized: boolean;
  immersiveInputFocused: boolean;
  editMode: EditMode;
  transformMode: TransformMode;
  lodMap: Map<string, LODLevel>;
  mobileToolbarVisible: boolean;
  hasSeenOrientationHint: boolean;

  selectObject: (id: string) => void;
  clearSelection: () => void;
  clearSelectionIf: (id: string) => void;
  setModelLibraryOpen: (open: boolean) => void;
  toggleModelLibrary: () => void;
  setSidePanelOpen: (open: boolean) => void;
  toggleSidePanel: () => void;
  setSidePanelTab: (tab: SidePanelTab) => void;
  setDragPreview: (preview: DragPreviewState | null) => void;
  updateDragPosition: (position: [number, number, number] | null) => void;
  setImmersiveMode: (enabled: boolean) => void;
  toggleImmersiveMode: () => void;
  setImmersiveToolbarMinimized: (minimized: boolean) => void;
  toggleImmersiveToolbarMinimized: () => void;
  setImmersiveInputFocused: (focused: boolean) => void;
  enterEditMode: () => void;
  exitEditMode: () => void;
  toggleEditMode: () => void;
  setTransformMode: (mode: TransformMode) => void;
  setLodMap: (map: Map<string, LODLevel>) => void;
  setMobileToolbarVisible: (visible: boolean) => void;
  dismissOrientationHint: () => void;
  resetForSpaceLoad: () => void;
}

export const useUiStore = create<UiStore>((set) => ({
  selectedObjectId: null,
  isTransformDragging: false,
  modelLibraryOpen: false,
  sidePanelOpen: false,
  sidePanelTab: "models",
  dragPreview: null,
  immersiveMode: true,
  immersiveToolbarMinimized: false,
  immersiveInputFocused: false,
  editMode: "readonly",
  transformMode: "translate",
  lodMap: new Map(),
  mobileToolbarVisible: true,
  hasSeenOrientationHint:
    typeof window !== "undefined" &&
    localStorage.getItem("orymia-orientation-hint-seen") === "1",

  selectObject: (id) =>
    set({ selectedObjectId: id, sidePanelTab: "object", sidePanelOpen: true }),

  clearSelection: () => set({ selectedObjectId: null }),

  clearSelectionIf: (id) =>
    set((state) => ({
      selectedObjectId:
        state.selectedObjectId === id ? null : state.selectedObjectId,
    })),

  setModelLibraryOpen: (open) =>
    set({
      modelLibraryOpen: open,
      sidePanelOpen: open,
      sidePanelTab: "models",
    }),

  toggleModelLibrary: () =>
    set((state) => ({
      modelLibraryOpen: !state.modelLibraryOpen,
      sidePanelOpen: state.modelLibraryOpen ? state.sidePanelOpen : true,
      sidePanelTab: "models",
    })),

  setSidePanelOpen: (open) =>
    set({ sidePanelOpen: open, modelLibraryOpen: open }),

  toggleSidePanel: () =>
    set((state) => ({
      sidePanelOpen: !state.sidePanelOpen,
      modelLibraryOpen: !state.sidePanelOpen,
    })),

  setSidePanelTab: (tab) =>
    set({ sidePanelTab: tab, sidePanelOpen: true, modelLibraryOpen: true }),

  setDragPreview: (preview) => set({ dragPreview: preview }),

  updateDragPosition: (position) =>
    set((state) => ({
      dragPreview: state.dragPreview
        ? { ...state.dragPreview, position }
        : null,
    })),

  setImmersiveMode: (enabled) => set({ immersiveMode: enabled }),
  toggleImmersiveMode: () =>
    set((state) => ({ immersiveMode: !state.immersiveMode })),
  setImmersiveToolbarMinimized: (minimized) =>
    set({ immersiveToolbarMinimized: minimized }),
  toggleImmersiveToolbarMinimized: () =>
    set((state) => ({
      immersiveToolbarMinimized: !state.immersiveToolbarMinimized,
    })),
  setImmersiveInputFocused: (focused) =>
    set({ immersiveInputFocused: focused }),

  enterEditMode: () => set({ editMode: "editing", sidePanelOpen: false }),
  exitEditMode: () => set({ editMode: "readonly", sidePanelOpen: false }),
  toggleEditMode: () =>
    set((state) => ({
      editMode: state.editMode === "readonly" ? "editing" : "readonly",
      sidePanelOpen: false,
    })),

  setTransformMode: (mode) => set({ transformMode: mode }),
  setLodMap: (map) => set({ lodMap: map }),
  setMobileToolbarVisible: (visible) => set({ mobileToolbarVisible: visible }),

  dismissOrientationHint: () => {
    try {
      localStorage.setItem("orymia-orientation-hint-seen", "1");
    } catch {
      // localStorage may be unavailable
    }
    set({ hasSeenOrientationHint: true });
  },

  resetForSpaceLoad: () =>
    set({
      selectedObjectId: null,
      editMode: "readonly",
    }),
}));
